/**
 * TablePage - Page component that manages a TableView with URL parameter synchronization
 *
 * A clean, simplified implementation using the new TableView component.
 * Automatically syncs pagination, sorting, and filtering with URL parameters.
 *
 * @example
 * const usersPage = new TablePage({
 *   pageName: 'users',
 *   title: 'User Management',
 *   collection: userCollection,
 *   columns: [
 *     { key: 'name', label: 'Name', sortable: true },
 *     { key: 'email', label: 'Email' },
 *     { key: 'role', label: 'Role', type: 'badge' }
 *   ],
 *   actions: ['view', 'edit', 'delete']
 * });
 */

import Page from '@core/Page.js';
import Modal from '@core/views/feedback/Modal.js';
import TableView from '@core/views/table/TableView.js';
import Collection from '@core/Collection.js';
import { parseFilterKey } from '@core/utils/DjangoLookups.js';

class TablePage extends Page {
  constructor(options = {}) {
    super({
      ...options,
      pageName: options.pageName || options.name || 'table'
    });

    // Page configuration
    this.title = options.title || this.pageName;
    this.description = options.description || '';

    // Collection setup
    this.Collection = options.Collection || null;
    this.collection = options.collection || null;

    // our default collection query
    this.defaultQuery = options.defaultQuery || {};

    // Group field configuration - defaults to "group"
    this.groupField = options.groupField || 'group';

    // Store configuration for TableView
    // Map legacy property names to new ones
    this.tableViewConfig = {
      // Core table properties
      columns: options.columns || [],
      actions: options.actions || null,
      contextMenu: options.contextMenu || options.rowContextMenu || null,
      batchActions: options.batchActions || null,
      batchBarLocation: options.batchBarLocation || 'top',
      clickAction: options.clickAction || 'view',
      // Map legacy form properties to new names
      addForm: options.addForm || options.formFields || options.formCreate,
      editForm: options.editForm || options.formEdit || options.formFields,

      // Model operation configurations
      itemView: options.itemView || options.itemViewClass,
      deleteTemplate: options.deleteTemplate,
      formDialogConfig: options.formDialogConfig,
      viewDialogOptions: options.viewDialogOptions,

      // Features
      searchable: options.searchable !== false,
      sortable: options.sortable !== false,
      filterable: options.filterable !== false,
      paginated: options.paginated !== false,

      // Selection mode
      selectionMode: options.selectionMode || (options.selectable ? 'multiple' : 'none'),

      // Filter configuration
      filters: options.filters || options.additionalFilters || [],
      hideActivePills: options.hideActivePills || false,
      hideActivePillNames: options.hideActivePillNames || [],
      searchPlacement: options.searchPlacement || 'toolbar',

      // Toolbar day-range segment + chronological grouping. ListView /
      // TableView own the implementation; TablePage just forwards.
      dayRangeFilter: options.dayRangeFilter,
      groupBy: options.groupBy,
      groupHeaderTemplate: options.groupHeaderTemplate,
      groupHeaderLabel: options.groupHeaderLabel,
      groupHeaderStyle: options.groupHeaderStyle,

      // Row stripe — same forwarding pattern.
      rowStripe: options.rowStripe,

      // Filter presets (WM-032) — same forwarding pattern.
      filterPresets: options.filterPresets,

      // Expandable detail rows (WM-036) — same forwarding pattern.
      rowExpand: options.rowExpand,
      rowExpandMultiple: options.rowExpandMultiple,

      // Feedback states (WM-033) — same forwarding pattern.
      emptyState: options.emptyState,
      loadingStyle: options.loadingStyle,
      showResultCount: options.showResultCount,

      // Auto-refresh (WM-034) — same forwarding pattern.
      autoRefresh: options.autoRefresh,

      // View persistence + column chooser (WM-035) — same forwarding pattern.
      persistState: options.persistState,
      persistKey: options.persistKey,
      columnChooser: options.columnChooser,

      // Display options for the HTML table element
      tableOptions: {
        striped: true,
        bordered: false,
        hover: true,
        responsive: false,
        ...options.tableOptions
      },

      // Additional options
      emptyMessage: options.emptyMessage || 'No data available',
      searchPlaceholder: options.searchPlaceholder || 'Search...',
      showAdd: options.showAdd !== false,
      showExport: options.showExport !== false,

      // Custom handlers
      onItemView: options.onItemView,
      onItemEdit: options.onItemEdit,
      onItemDelete: options.onItemDelete,
      onAdd: options.onAdd,
      onExport: options.onExport,

      // Override with tableViewOptions if provided
      ...options.tableViewOptions
    };

    // URL synchronization
    this.urlSyncEnabled = options.urlSyncEnabled !== false;

    // Status tracking
    this.lastUpdated = null;
    this.isLoading = false;

    // Set up template
    this.template = options.template || this.buildTemplate();
  }

  /**
   * Build the page template
   */
  buildTemplate() {
    return `
      <div class="table-page-container">

        <div class="table-container" data-container="table"></div>

        {{#showStatus}}
          <div class="table-status-bar table-status-top">
            <div class="status-info">
              <div class="d-flex justify-content-between w-100">
                <span class="text-muted">
                  <i class="bi bi-clock me-1"></i>
                  Last updated: <span data-status="last-updated">{{lastUpdated}}</span>
                </span>
                <span class="text-muted">
                  <i class="bi bi-list-ol me-1"></i>
                  Total records: <span data-status="record-count">0</span>
                </span>
              </div>
            </div>
          </div>
        {{/showStatus}}

      </div>
    `;
  }

  /**
   * Initialize the page
   */
  async onInit() {
    await super.onInit();

    // Create collection if needed
    if (!this.collection) {
      if (this.Collection) {
        this.collection = new this.Collection();
      } else {
        this.collection = new Collection();
      }
    }

    // Apply URL query parameters to collection
    this.applyQueryToCollection();

    // Create TableView instance with all configuration
    // Intercept item view to add deep-link URL support
    this.tableView = new TableView({
      collection: this.collection,
      containerId: 'table',
      fetchOnMount: true,
      ...this.tableViewConfig,
      onItemView: async (model, event) => {
        // Allow subclass onItemView to fully override
        if (this.tableViewConfig.onItemView) {
          return this.tableViewConfig.onItemView(model, event);
        }
        await this.showItemDialog(model);
      }
    });

    // Add as child view
    this.addChild(this.tableView);

    // Set up event listeners
    this.setupEventListeners();
  }

  /**
   * Set up event listeners
   */
  setupEventListeners() {
    // Listen for collection changes to sync URL
    if (this.urlSyncEnabled && this.collection) {
      // Sync URL when collection params change
      this.collection.on('fetch:start', () => {
        this.isLoading = true;
      });

      this.collection.on('fetch:end', () => {
        this.isLoading = false;
        this.lastUpdated = new Date().toLocaleTimeString();
        this.updateStatusDisplay();
      });
    }

    // Listen for params-changed event from TableView to sync URL
    this.tableView.on('params-changed', () => {
      if (this.urlSyncEnabled) {
        this.syncUrl();
      }
    });

    // // Listen for table events (these also emit params-changed, but keep for backwards compatibility)
    // this.tableView.on('table:search', ({ searchTerm }) => {
    //   // params-changed will handle URL sync
    // });

    // this.tableView.on('table:sort', ({ field }) => {
    //   // params-changed will handle URL sync
    // });

    // this.tableView.on('table:page', ({ page }) => {
    //   // params-changed will handle URL sync
    // });

    // Filter:edit handling lives entirely on ListView/TableView itself
    // (it owns the edit-modal flow). We do NOT listen for `filter:edit`
    // here anymore — doing so opened a second racing Modal.form. Filter
    // changes still arrive via the `params-changed` event, which drives
    // the URL sync below.

    // Row action events
    this.tableView.on('row:view', async ({ model }) => {
      if (this.onItemView) {
        await this.onItemView(model);
      }
    });

    this.tableView.on('row:edit', async ({ model }) => {
      if (this.onItemEdit) {
        await this.onItemEdit(model);
      }
    });

    this.tableView.on('row:delete', async ({ model }) => {
      if (this.onItemDelete) {
        await this.onItemDelete(model);
      }
    });

    // Table action events
    // Note: TableView will call options.onAdd if provided, but we still listen to the event
    // for backwards compatibility and to support event-based patterns
    this.tableView.on('table:add', async ({ event }) => {
      // The handler was already called by TableView if options.onAdd is set,
      // but we keep this listener for external code that might listen to 'table:add'
      // We don't call this.tableViewConfig.onAdd here to avoid double execution
    });

    this.tableView.on('table:export', async ({ data }) => {
      if (this.tableViewConfig.onExport) {
        await this.tableViewConfig.onExport(data);
      }
    });
  }

  /**
   * Apply URL query parameters to collection
   */
  applyQueryToCollection() {
    const params = {};
    const query = { ...this.defaultQuery, ...this.query };
    if (!query || Object.keys(query).length === 0) {
        return;
    }
    // Pagination
    if (query.start !== undefined) params.start = parseInt(query.start) || 0;
    if (query.size !== undefined) params.size = parseInt(query.size) || 10;

    // Sorting
    if (query.sort !== undefined) params.sort = query.sort;

    // Search
    if (query.search !== undefined) params.search = query.search;

    // Process all other params as potential filters
    const reservedParams = ['start', 'size', 'sort', 'search', 'page', '_item'];
    Object.entries(query).forEach(([key, value]) => {
      if (!reservedParams.includes(key) && value !== undefined && value !== '') {
        // Parse value if it looks like JSON
        if (typeof value === 'string' && (value.startsWith('{') || value.startsWith('['))) {
          try {
            params[key] = JSON.parse(value);
          } catch (e) {
            params[key] = value;
          }
        } else {
          params[key] = value;
        }
      }
    });

    // Update collection params
    if (Object.keys(params).length > 0) {
      // Deduplicate simple + __in filters (prefer __in / not_in when both are present)
      Object.keys(params).forEach(key => {
        const { field, lookup } = parseFilterKey(key);
        if ((lookup === 'in' || lookup === 'not_in') && params.hasOwnProperty(field)) {
          delete params[field];
        }
      });

      this.collection.setParams({
        ...this.collection.params,
        ...params
      });
    }
  }

  /**
   * Sync URL with current table state
   */
  syncUrl(force = true) {
    if (!this.urlSyncEnabled || !this.collection || !this.getApp()?.router) {
      return;
    }

    // Get current URL params
    const currentUrl = new URL(window.location);
    const currentParams = {};
    for (const [key, value] of currentUrl.searchParams) {
      if (key !== 'page') {
        currentParams[key] = value;
      }
    }

    // Get desired params from collection
    const desiredParams = {};
    const collectionParams = this.collection.params || {};

    // Only include non-default values
    if (collectionParams.start) {
      desiredParams.start = collectionParams.start;
    }
    if (collectionParams.size) {
      desiredParams.size = collectionParams.size;
    }
    if (collectionParams.sort) {
      desiredParams.sort = collectionParams.sort;
    }
    if (collectionParams.search) {
      desiredParams.search = collectionParams.search;
    }

    // Include other filters
    Object.entries(collectionParams).forEach(([key, value]) => {
      if (!['start', 'size', 'sort', 'search'].includes(key) && value !== undefined && value !== '') {
        // Stringify complex values for URL
        if (typeof value === 'object') {
          desiredParams[key] = JSON.stringify(value);
        } else {
          desiredParams[key] = value;
        }
      }
    });

    // Check if there are any changes
    const hasChanges =
      Object.keys(desiredParams).some(key =>
        String(currentParams[key] || '') !== String(desiredParams[key] || '')
      ) ||
      Object.keys(currentParams).some(key =>
        !(key in desiredParams)
      );

    // Preserve _item param if currently set (deep-link, not a collection param)
    if (this.query._item) {
      desiredParams._item = this.query._item;
    }

    this.query = desiredParams;
    if (!hasChanges && !force) return;

    // Update URL
    this.updateBrowserUrl(desiredParams, true, false);
  }

  /**
   * Update status display
   */
  updateStatusDisplay() {
    if (!this.element) return;

    // Update last updated time
    const updatedElement = this.element.querySelector('[data-status="last-updated"]');
    if (updatedElement) {
      updatedElement.textContent = this.lastUpdated || 'Never';
    }

    // Update record count
    const countElement = this.element.querySelector('[data-status="record-count"]');
    if (countElement && this.collection) {
      const count = this.collection.meta?.count || this.collection.length();
      countElement.textContent = count;
    }
  }

  /**
   * Called when entering this page
   */
  async onEnter() {
    await super.onEnter();

    if (this.options.requiresGroup && !this.query[this.groupField] && this.getApp().activeGroup) {
      this.query[this.groupField] = this.getApp().activeGroup.id;
    }

    this.applyQueryToCollection();

    // Ensure filter pills are shown if there are active filters from URL
    if (this.tableView && this.tableView.element) {
      setTimeout(() => {
        this.tableView.updateFilterPills();
        this.tableView.updateSortIcons();
      }, 100);
    }

    // Deep-link: auto-open item dialog if _item param is present
    if (this.query._item) {
      this._openDeepLinkedItem(this.query._item);
    }
  }

  /**
   * Open a deep-linked item dialog by fetching the model and showing it
   */
  async _openDeepLinkedItem(itemId) {
    try {
      const model = await this.collection.fetchOne(itemId);
      if (model) {
        await this.showItemDialog(model);
      }
    } catch (e) {
      // Item not found or fetch failed — silently clear the param
      this._clearItemParam();
    }
  }

  /**
   * Show the item view dialog with deep-link URL support
   */
  async showItemDialog(model) {
    // Update URL with _item param
    this._setItemParam(model.id);

    // Fetch latest model data before showing the dialog
    if (this.tableView.fetchOnView) {
      try {
        Modal.loading();
        await model.fetch();
      } catch (error) {
        Modal.hideLoading(true);
        Modal.showError(error?.data?.error || error?.message || 'Failed to load item details');
        this._clearItemParam();
        return;
      } finally {
        Modal.hideLoading(true);
      }
    }

    const ViewClass = this.tableView.getItemViewClass(model);

    if (ViewClass) {
      const viewInstance = new ViewClass({ model, collection: this.collection });
      await Modal.dialog({
        header: false,
        body: viewInstance,
        size: 'lg',
        centered: false,
        ...this.tableView.getFormDialogConfig(this.tableView.getModelClass(model)),
        // View class declares its own modal presentation via static DIALOG_OPTIONS.
        ...ViewClass.DIALOG_OPTIONS,
        ...this.tableView.viewDialogOptions
      });
    } else {
      await Modal.data({
        title: `View ${this.tableView.getModelName(model)} #${model.id}`,
        model
      });
    }

    // Dialog closed — remove _item from URL
    this._clearItemParam();
  }

  /**
   * Add _item param to URL
   */
  _setItemParam(itemId) {
    const params = { ...this.query, _item: itemId };
    this.query = params;
    this.updateBrowserUrl(params, true, false);
  }

  /**
   * Remove _item param from URL
   */
  _clearItemParam() {
    delete this.query._item;
    this.updateBrowserUrl(this.query, true, false);
  }

  /**
   * Public method to refresh the table
   */
  async refresh() {
    await this.tableView.refresh();
  }

  /**
   * Public method to get selected items
   */
  getSelectedItems() {
    return this.tableView.getSelectedItems();
  }

  /**
   * Public method to clear selection
   */
  clearSelection() {
    this.tableView.clearSelection();
  }

  /**
   * Batch-action helper — encapsulates the confirm → save/destroy → toast →
   * refresh pattern shared by every admin batch handler.
   *
   * Pass one of three modes:
   *   - { field, value, label, message? }            → save({ [field]: value }) on each
   *   - { destroy: true, label, message? }           → destroy() each
   *   - { handler: async (model) => …, label, … }    → run handler(model) per item
   *
   * Behavior:
   *   - Resolves to 0 (no-op) when the selection is empty.
   *   - Confirms via Modal.confirm unless `confirm: false` is passed.
   *   - Runs items in parallel via Promise.allSettled so one failure
   *     doesn't abort the rest.
   *   - Surfaces a ToastService message — success-only or "N succeeded,
   *     M failed" — based on the settled outcomes.
   *   - Always clears selection and refreshes the table on a non-cancel
   *     path so the user sees the partial state on failure.
   *
   * @param {object} options
   * @param {string} [options.field] - Field name to set on each model (save mode).
   * @param {*}      [options.value] - Value to assign when in save mode.
   * @param {boolean}[options.destroy=false] - Destroy each model instead of saving.
   * @param {Function}[options.handler] - Custom async (model) => * handler.
   * @param {string} [options.label='Action'] - Verb used in the confirm + toast.
   * @param {string} [options.message] - Override the confirm message.
   * @param {boolean}[options.confirm=true] - Skip the confirm prompt when false.
   * @returns {Promise<number>} The count of successful operations.
   */
  async batchAction({
    field,
    value,
    destroy = false,
    handler = null,
    label = 'Action',
    message,
    confirm = true
  } = {}) {
    const items = this.tableView ? this.tableView.getSelectedItems() : [];
    if (!items.length) return 0;

    if (confirm) {
      const ok = await Modal.confirm(
        message || `${label} ${items.length} item(s)?`
      );
      if (!ok) return 0;
    }

    const results = await Promise.allSettled(items.map(({ model }) => {
      if (handler) return handler(model);
      if (destroy) return model.destroy();
      return model.save({ [field]: value });
    }));

    const successes = results.filter(r => r.status === 'fulfilled').length;
    const failures = results.length - successes;

    const app = this.getApp ? this.getApp() : null;
    if (failures === 0) {
      app?.toast?.success?.(`${label}: ${successes} item(s) updated`);
    } else if (successes === 0) {
      app?.toast?.error?.(`${label} failed for all ${failures} item(s)`);
    } else {
      app?.toast?.warning?.(
        `${label}: ${successes} succeeded, ${failures} failed`
      );
    }

    this.tableView.clearSelection();
    await this.tableView.refresh();
    return successes;
  }

  /**
   * Handle filter edit dialog
   */
  async handleFilterEdit(filterKey) {
    const filterConfig = this.tableView.getAllAvailableFilters().find(f => f.key === filterKey);
    const currentValue = this.collection.params[filterKey];

    if (!filterConfig) return;

    // Build form field for the filter
    const field = {
      name: 'filter_value',
      label: filterConfig.label || filterKey,
      value: currentValue,
      ...filterConfig.config
    };

    const result = await Modal.form({
      title: `Edit ${field.label} Filter`,
      size: 'md',
      fields: [field]
    });

    if (result && result.filter_value !== undefined) {
      this.tableView.setFilter(filterKey, result.filter_value);

      if (this.collection.restEnabled) {
        this.collection.fetch();
      }
      await this.tableView.render();
      this.syncUrl();
    }
  }

  /**
   * Clear all filters
   */
  clearAllFilters() {
    if (!this.collection) return;

    // Keep only pagination and sort params
    const { start, size, sort } = this.collection.params;
    this.collection.params = { start, size };
    if (sort) this.collection.params.sort = sort;

    this.syncUrl();

    if (this.collection.restEnabled) {
      this.collection.fetch();
    } else {
      this.tableView.render();
    }
  }

  async onGroupChange(group) {
      if (!group || !this.collection || !this.options.requiresGroup) return;
      this.query[this.groupField] = group.id;
      this.applyQueryToCollection();
      if (this.collection && this.collection.restEnabled) {
        this.collection.fetch();
      }
  }

  /**
   * Cleanup on destroy
   */
  async onBeforeDestroy() {
    // Remove event listeners
    if (this.collection) {
      this.collection.off('fetch:start');
      this.collection.off('fetch:end');
    }

    if (this.tableView) {
      this.tableView.off('params-changed');
      this.tableView.off('table:search');
      this.tableView.off('table:sort');
      this.tableView.off('table:page');
      this.tableView.off('row:view');
      this.tableView.off('row:edit');
      this.tableView.off('row:delete');
      this.tableView.off('table:add');
      this.tableView.off('table:export');
    }

    await super.onBeforeDestroy();
  }

  /**
   * Show/hide status display
   */
  get showStatus() {
    return this.options.showStatus === true;
  }

  /**
   * Static factory method
   */
  static create(options = {}) {
    return new this(options);
  }
}

export default TablePage;
